/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { DEFAULT_MIGRATION_SETTINGS, MigrationSettings } from './migration.models';

/**
 * Deployment-wide migration settings. Two audiences read them: the wizard
 * uses the policy/preserve fields as defaults for new drafts, and the engine
 * reads the file at run start for its knobs (batch size → status write-back
 * cadence, workers → parallel documents, retries → per-item attempts,
 * log level → per-item logging). The form stays disabled without the engine —
 * values that nothing executes must not pretend to work.
 */
@Component({
  selector: 'app-mig-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-settings.component.html'
})
export class MigrationSettingsComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  readonly api = inject(MigrationApiService);

  readonly busy = signal(false);
  settings: MigrationSettings = { ...DEFAULT_MIGRATION_SETTINGS };

  ngOnInit(): void {
    this.service.settings().subscribe((s) => (this.settings = s));
  }

  /** Editable only while the engine exists to honor the values. */
  get editable(): boolean {
    return this.api.available() === true;
  }

  save(): void {
    if (!this.editable) {
      return;
    }
    this.busy.set(true);
    this.service.saveSettings(this.settings).subscribe({
      next: () => {
        this.busy.set(false);
        this.notification.showInfo(this.translate.instant('DMS.MIGRATION.SETTINGS_SAVED'));
      },
      error: () => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }
}
